<template>
  <div id="phrase-library">
    <UserPhraseLibraryForm
      v-model:shorthandValue="shorthandValue"
      v-model:expandedValue="expandedValue"
      :form-errors="errors.formErrors"
      @on-save="handleOnSave"
    />
    <UserPhraseLibraryTable
      :phrases="phrases"
      :is-loading="isLoading"
      @on-delete="handleOnDelete"
      @on-edit="handleOnEdit"
    />
    <UserPhraseLibraryModal
      :show="showEditModal"
      :phrase="editingPhrase"
      :form-errors="modalErrors.formErrors"
      @close="closeEditModal"
      @on-save="handleModalSave"
    />
  </div>
</template>

<script setup lang="ts">
import UserPhraseLibraryForm from "./UserPhraseLibraryForm.vue";
import { reactive, ref, onMounted } from "vue";
import UserPhraseLibraryTable from "./UserPhraseLibraryTable.vue";
import UserPhraseLibraryModal from "./UserPhraseLibraryModal.vue";
import { practitionerHttpService } from "../../../../../app/practitioner-portal/store/practitioner-http-service";
import Notifications from "../../../../Utils/notifications";
import { Phrase } from "../../../../Utils/phraselibrary";

const phrases = ref<Phrase[]>([]);
const isLoading = ref(false);

const shorthandValue = ref("");
const expandedValue = ref("");
const errors = reactive({
  formErrors: {
    shorthandErrors: null as string[] | null,
    expandedErrors: null as string[] | null,
  },
});
const modalErrors = reactive({
  formErrors: {
    shorthandErrors: null as string[] | null,
    expandedErrors: null as string[] | null,
  },
});

const showEditModal = ref(false);
const editingPhrase = ref<Partial<Phrase> | null>(null);

const clearAllErrors = () => {
  errors.formErrors.shorthandErrors = null;
  errors.formErrors.expandedErrors = null;
  modalErrors.formErrors.shorthandErrors = null;
  modalErrors.formErrors.expandedErrors = null;
};

// ---Lifecycle hooks --- //
onMounted(() => {
  fetchPhrases();
});
const fetchPhrases = async () => {
  isLoading.value = true;
  try {
    const response = await practitionerHttpService.get("/api/internal/phrases");
    phrases.value = response.data?.phrases;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const handleOnSave = async (newPhrase: {
  shorthandValue: string;
  expandedValue: string;
}) => {
  clearAllErrors();

  try {
    const payload = {
      shorthand: newPhrase.shorthandValue,
      expanded: newPhrase.expandedValue,
    };

    const response = await practitionerHttpService.post(
      "/api/internal/phrases",
      payload,
    );
    const createdPhrase = response.data[0];

    if (createdPhrase && createdPhrase.id) {
      phrases.value.unshift({ ...createdPhrase, _highlight: true });
      shorthandValue.value = "";
      expandedValue.value = "";
      Notifications.saveSuccess();

      setTimeout(() => {
        if (phrases.value[0]?._highlight) {
          phrases.value[0]._highlight = false;
        }
      }, 4000);

      return true;
    } else {
      console.error("Invalid phrase object.");
      return false;
    }
  } catch (error) {
    console.error("Failed to save phrase: ", error);
    if (error?.response?.status === 422) {
      const backendErrors = error.response.data;
      errors.formErrors.shorthandErrors = backendErrors?.shorthand ?? null;
      errors.formErrors.expandedErrors = backendErrors?.expanded ?? null;
    }
    return false;
  }
};

const handleModalSave = async (updatedPhrase: Phrase) => {
  clearAllErrors();

  try {
    const payload = {
      id: updatedPhrase.id,
      shorthand: updatedPhrase.shorthand,
      expanded: updatedPhrase.expanded,
    };

    const response = await practitionerHttpService.post(
      "/api/internal/phrases",
      payload,
    );
    const updated = response.data[0];

    phrases.value = phrases.value.map((phrase) =>
      phrase.id === updated.id ? updated : phrase,
    );
    Notifications.saveSuccess();
    closeEditModal();
  } catch (error) {
    console.error("Failed to update phrase: ", error);
    if (error?.response?.status === 422) {
      const backendErrors = error.response.data;
      modalErrors.formErrors.shorthandErrors = backendErrors?.shorthand ?? null;
      modalErrors.formErrors.expandedErrors = backendErrors?.expanded ?? null;
    }
  }
};

const handleOnEdit = (phrase: Phrase) => {
  clearAllErrors();

  editingPhrase.value = { ...phrase };
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingPhrase.value = null;
  clearAllErrors();
};

const handleOnDelete = async (id: number) => {
  try {
    await practitionerHttpService.delete(`/api/internal/phrases/${id}`);
    Notifications.saveSuccess();

    phrases.value = phrases.value.filter((p) => p.id !== id);
  } catch (error) {
    console.error(`Failed to delete phrase with id ${id}:`, error);
  }
};
</script>
